import React, { useState, useEffect, useCallback, useRef } from 'react';
import { HelpPanel, Pagination, Alert } from '@amzn/awsui-components-core';
import { RecentMeeting, AttendeePriority, NotificationActions } from '../../interfaces/interface';
import { UserEAConfig } from '../../services/UserEAConfigService';
import { useAuth } from '../../../../context/AuthContextProvider';
import { useImpersonation } from '../../../../context/ImpersonationContext';
import { SidebarHeader } from './SidebarHeader';
import { FavoritesSection } from './FavoritesSection';
import { MyMeetingsSection } from './MyMeetingsSection';
import { MySettingsSection } from './MySettingsSection';
import styles from './Sidebar.module.scss';

// ══════════════════════════════════════════════════════════════════════════════
// Sidebar
// ══════════════════════════════════════════════════════════════════════════════

interface SidebarProps {
  favoriteLoginNames: Set<string>;
  existingLoginNames: Set<string>;
  onFavoriteClick: (loginName: string, priority?: AttendeePriority) => void;
  recentMeetings: RecentMeeting[];
  totalMeetingCount: number;
  onDeleteMeeting: (meetingId: string) => Promise<void>;
  isReadOnly?: boolean;
  isMeetingRegistered?: boolean;
  notificationActions: NotificationActions;
  isRefreshing?: boolean;
  onRefresh?: () => void;
  onNewMeeting?: () => void;
  userEAConfigs?: UserEAConfig[];
  onSaveUserEAConfig?: (config: UserEAConfig) => Promise<void>;
  onDeleteUserEAConfig?: (leaderAlias: string) => Promise<void>;
}

// Fallback card height estimate (used before measurement is available)
const FALLBACK_CARD_HEIGHT = 80;
const DEFAULT_MAX_MEETINGS = 6;

export const Sidebar: React.FC<SidebarProps> = ({
  favoriteLoginNames,
  existingLoginNames,
  onFavoriteClick,
  recentMeetings,
  totalMeetingCount,
  onDeleteMeeting,
  isReadOnly = false,
  isMeetingRegistered = false,
  notificationActions,
  isRefreshing = false,
  onRefresh,
  onNewMeeting,
  userEAConfigs = [],
  onSaveUserEAConfig,
  onDeleteUserEAConfig
}) => {
  const { isHuddlePlusAdmin } = useAuth();
  const { impersonation, clearImpersonation } = useImpersonation();
  const [maxMeetings, setMaxMeetings] = useState(DEFAULT_MAX_MEETINGS);
  const [localPage, setLocalPage] = useState(1);

  // Refs for measuring actual rendered heights
  const meetingsContainerRef = useRef<HTMLDivElement>(null);
  const scrollableRef = useRef<HTMLDivElement>(null);

  // Measure how many meeting cards fit in the available space
  const recalculate = useCallback(() => {
    const scrollable = scrollableRef.current;
    const meetingsContainer = meetingsContainerRef.current;
    if (!scrollable) {
      setMaxMeetings(DEFAULT_MAX_MEETINGS);
      return;
    }

    const cards = meetingsContainer?.querySelectorAll('[data-meeting-card]');
    let avgCardHeight = FALLBACK_CARD_HEIGHT;

    if (cards && cards.length > 0) {
      const totalCardsHeight = Array.from(cards).reduce((sum, card) => {
        const rect = card.getBoundingClientRect();
        return sum + rect.height;
      }, 0);
      avgCardHeight = totalCardsHeight / cards.length + 8;
    }

    const scrollableHeight = scrollable.getBoundingClientRect().height;
    const scrollableTop = scrollable.getBoundingClientRect().top;
    const meetingsTop = meetingsContainer ? meetingsContainer.getBoundingClientRect().top : scrollableTop;
    const spaceAboveMeetings = meetingsTop - scrollableTop;
    // Buffer reserved for the pagination row and surrounding breathing room.
    const sectionPadding = 80;

    const availableForCards = scrollableHeight - spaceAboveMeetings - sectionPadding;
    const fits = Math.max(1, Math.floor(availableForCards / avgCardHeight));

    setMaxMeetings(fits);
  }, []);

  // Observe the scrollable container for size changes
  useEffect(() => {
    const el = scrollableRef.current;
    if (!el) return;

    const observer = new ResizeObserver(() => recalculate());
    observer.observe(el);
    window.addEventListener('resize', recalculate);
    requestAnimationFrame(recalculate);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', recalculate);
    };
  }, [recalculate]);

  // Recalculate when layout changes or meetings load. The Favorites set is
  // also tracked here because removing favorites shrinks the chrome above the
  // meeting cards — without this dep the meeting count stays stale until the
  // viewport itself resizes (the ResizeObserver only watches scrollable height,
  // not internal content shifts).
  useEffect(() => {
    const timer = setTimeout(recalculate, 50);
    return () => clearTimeout(timer);
  }, [recalculate, recentMeetings.length, favoriteLoginNames.size, userEAConfigs.length]);

  // Local pagination
  const meetingPageSize = maxMeetings;
  const meetingTotalPages = meetingPageSize > 0 ? Math.max(1, Math.ceil(recentMeetings.length / meetingPageSize)) : 1;
  const safePage = Math.min(localPage, meetingTotalPages);
  const showPagination = meetingTotalPages > 1;

  useEffect(() => {
    if (localPage > meetingTotalPages) setLocalPage(1);
  }, [meetingTotalPages, localPage]);

  const startIdx = (safePage - 1) * meetingPageSize;
  const pagedMeetings = recentMeetings.slice(startIdx, startIdx + meetingPageSize);

  return (
    <HelpPanel>
      <div className={styles.sidebar}>
        <SidebarHeader
          isRefreshing={isRefreshing}
          onRefresh={onRefresh}
          notificationActions={notificationActions}
          isHuddlePlusAdmin={isHuddlePlusAdmin}
        />

        {impersonation && (
          <Alert type="info" dismissible onDismiss={clearImpersonation}>
            Viewing as <strong>{impersonation.alias}</strong>
          </Alert>
        )}

        <div className={styles.scrollableContent} ref={scrollableRef}>
          <FavoritesSection
            favoriteLoginNames={favoriteLoginNames}
            existingLoginNames={existingLoginNames}
            onFavoriteClick={onFavoriteClick}
            isReadOnly={isReadOnly}
            isMeetingRegistered={isMeetingRegistered}
          />

          <div>
            <MySettingsSection userEAConfigs={userEAConfigs} onSaveUserEAConfig={onSaveUserEAConfig} onDeleteUserEAConfig={onDeleteUserEAConfig} />
          </div>

          <div ref={meetingsContainerRef} className={styles.meetingsContainer}>
            <MyMeetingsSection
              recentMeetings={pagedMeetings}
              totalMeetingCount={totalMeetingCount}
              onDeleteMeeting={onDeleteMeeting}
              existingLoginNames={existingLoginNames}
              onFavoriteClick={onFavoriteClick}
              isReadOnly={isReadOnly}
              isMeetingRegistered={isMeetingRegistered}
              onNewMeeting={onNewMeeting}
            />

            {showPagination && (
              <div className={styles.paginationBar} role="navigation" aria-label="Meetings pagination">
                <Pagination
                  currentPageIndex={safePage}
                  pagesCount={meetingTotalPages}
                  onChange={({ detail }) => setLocalPage(detail.currentPageIndex)}
                  ariaLabels={{
                    nextPageLabel: 'Next page',
                    previousPageLabel: 'Previous page',
                    pageLabel: (pageNumber) => `Page ${pageNumber}`
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </HelpPanel>
  );
};
